import React, { Fragment, useRef } from "react";
import { Button, Form, InputRef, Select, SelectProps, Space, Tag } from "antd";
import {
  Controller,
  Control,
  FieldValues,
  PathValue,
  Path,
} from "react-hook-form";
import { InfoIconOutlined, SelectArrow, TickIcon } from "../../CustomIcons";
import { PlusOutlined } from "@ant-design/icons";
import CompanyIcon from "../../CustomIcons/CompanyIcon";
import IndividualIcon from "../../CustomIcons/IndividualIcon";

type TagRender = SelectProps["tagRender"];

const { Option } = Select;

interface materialOptionType {
  value: string;
  label: string;
  color?: string;
  type: string;
}

interface materialDropDownType {
  label: string;
  options: materialOptionType[];
}

interface SelectFieldProps<T extends FieldValues> {
  fieldName: Path<T>;
  label?: string;
  control: Control<T>;
  defaultValue?: PathValue<T, Path<T>> | undefined;
  iProps?: SelectProps<string | number | readonly string[]>;
  isRequired?: boolean;
  rules?: object;
  validate?: "" | "success" | "warning" | "error" | "validating" | undefined;
  validMessage?: string | React.ReactNode;
  className?: string;
  prefix?: React.ReactNode;
  tooltipTitle?: string | React.ReactNode; // Use ReactNode for tooltip title
  optionalLabel?: string;
  hintMessage?: string;
  warning?: boolean;
  onSelectChange?: (
    value: string | number | readonly string[],
    options: { value: string; label: string; color?: string; type: string }
  ) => void;
  selectOption: materialDropDownType[];
  addItemText: string;
  onAddItemClick?: () => void;
  differentClassName?: string;
}

export const SelectOptionWithButton = <T extends FieldValues>({
  fieldName,
  isRequired = false, // Provide default value
  label,
  validMessage,
  className,
  control,
  defaultValue, // Provide default value
  rules,
  iProps,
  prefix,
  tooltipTitle,
  optionalLabel,
  hintMessage,
  warning,
  validate,
  onSelectChange,
  selectOption,
  addItemText,
  onAddItemClick,
  differentClassName,
}: SelectFieldProps<T>) => {
  const inputRef = useRef<InputRef>(null);
  const optionMap = selectOption?.reduce<
    Record<string, { label: string; value: string; color?: string }>
  >((acc, optionGroup) => {
    optionGroup.options.forEach((option) => {
      acc[option.value] = option;
    });
    return acc;
  }, {});

  const tagRender: TagRender = (props) => {
    const { value } = props;
    const optionData = optionMap[value];
    const onPreventMouseDown = (event: React.MouseEvent<HTMLSpanElement>) => {
      event.preventDefault();
      event.stopPropagation();
    };
    return optionData?.color ? (
      <Tag
        className={optionData?.color ?? "default_tag"}
        onMouseDown={onPreventMouseDown}
      >
        {optionData?.label}
      </Tag>
    ) : (
      <div>{optionData?.label}</div>
    );
  };

  const addItem = (
    e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>
  ) => {
    e.preventDefault();
    if (onAddItemClick) {
      onAddItemClick();
    }
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  };

  return (
    <Form.Item
      required={isRequired ?? false}
      label={<div className="label_text">{label}</div>}
      validateStatus={validate}
      help={validMessage}
      className={className}
      tooltip={
        tooltipTitle && {
          title: tooltipTitle,
          placement: "topLeft",
          arrow: false,
          icon: <InfoIconOutlined />,
        }
      }
    >
      <Controller
        name={fieldName}
        control={control}
        defaultValue={defaultValue}
        rules={rules}
        render={({ field: { onChange, value } }) => (
          <>
            <Select
              popupClassName={`${differentClassName} option-group-select`}
              suffixIcon={<SelectArrow />}
              {...(iProps && { ...iProps, size: undefined })}
              value={value ? value : null}
              menuItemSelectedIcon={<TickIcon style={{ color: "#00A07D" }} />}
              showSearch
              optionFilterProp="label"
              prefix={prefix}
              onChange={(val) => {
                onChange(val);
                if (onSelectChange) {
                  selectOption?.some((innerArray) => {
                    const foundInInnerArray = innerArray?.options?.find(
                      (element: materialOptionType) => element?.value === val
                    );

                    if (foundInInnerArray) {
                      onSelectChange(val, foundInInnerArray);
                      return true; // Ensure a boolean is returned
                    }
                    return false; // Explicitly return false if not found
                  });
                }
              }}
              tagRender={tagRender}
              dropdownRender={(menu) => (
                <div>
                  {menu}
                  <Space
                    style={{
                      padding: "15px 12px 10px 12px",
                      borderRadius: "0px",
                      borderTop: "1px solid rgb(222, 229, 230)",
                      marginTop: "10px",
                      width: "100%",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <h6
                      style={{
                        fontWeight: 400,
                        fontSize: "12px",
                        lineHeight: "16px",
                        color: "#474C4D",
                      }}
                    >
                      No client you need? <br /> <span>Add new.</span>
                    </h6>
                    <Button
                      type="text"
                      icon={<PlusOutlined />}
                      onClick={addItem}
                      style={{
                        padding: "6px 12px",
                        borderRadius: "10px",
                        border: "1px solid rgb(222, 229, 230)",
                        color: "rgb(21, 23, 24)",
                        fontSize: "13px",
                        fontWeight: 600,
                        marginTop: "10px",
                        width: "100%",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        cursor: "pointer",
                      }}
                    >
                      {addItemText}
                    </Button>
                  </Space>
                </div>
              )}
            >
              {selectOption?.length > 0 &&
                selectOption?.map(
                  (
                    resp: {
                      label: string;
                      options: materialOptionType[];
                    },
                    i: number
                  ) => (
                    <Fragment key={i}>
                      {resp?.options?.length > 0 && (
                        <Option
                          value={resp?.label}
                          label={resp?.label}
                          disabled
                        >
                          {differentClassName &&
                          resp?.label === "Individuals" ? (
                            <IndividualIcon />
                          ) : (
                            <CompanyIcon />
                          )}
                          {resp?.label}
                        </Option>
                      )}

                      {resp?.options?.length > 0 &&
                        resp?.options?.map((e: materialOptionType) => {
                          return (
                            <Fragment key={e?.value}>
                              <Option value={e?.value} label={e?.label}>
                                {e?.color ? (
                                  <Tag
                                    style={{ padding: "2px 6px" }}
                                    className={e?.color ?? "default_tag"}
                                  >
                                    {e?.label}
                                  </Tag>
                                ) : (
                                  e?.label
                                )}
                              </Option>
                            </Fragment>
                          );
                        })}
                    </Fragment>
                  )
                )}
            </Select>
            {optionalLabel && (
              <div className="optional-label">{optionalLabel}</div>
            )}
            {hintMessage && (
              <div
                className={warning ? "hint-label warning-label" : "hint-label"}
              >
                {hintMessage}
              </div>
            )}
          </>
        )}
      />
    </Form.Item>
  );
};
